<template>
  <!-- titlePlacement === 'left' -->
  <div v-if="!isTitleOnTop" :style="leftTitleStyle">
    <div :style="leftTitleTextStyle">
      <slot name="title"><RenderNode :content="title" /></slot>
    </div>
    <JdButton
      v-if="collapsible"
      type="text"
      @click="emit('toggle')"
    >
      <template #icon>
        <component :is="collapsed ? AngleRight : AngleDown" :style="collapseIconStyle" />
      </template>
    </JdButton>
  </div>

  <!-- titlePlacement === 'top' -->
  <div v-else :style="topTitleStyle">
    <div :style="topTitleInnerStyle">
      <div :style="topTitleLabelStyle">
        <slot name="title"><RenderNode :content="title" /></slot>
      </div>
      <div v-if="hasDescription || $slots.description" :style="descriptionStyle">
        <slot name="description"><RenderNode :content="description" /></slot>
      </div>
    </div>
    <div v-if="hasExtra || $slots.extra || collapsible" :style="topExtraStyle">
      <slot name="extra"><RenderNode :content="extra" /></slot>
      <JdButton
        v-if="collapsible"
        type="text"
        @click="emit('toggle')"
      >
        <template #icon>
          <component :is="collapsed ? AngleRight : AngleDown" :style="collapseIconStyle" />
        </template>
      </JdButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineComponent, type CSSProperties } from 'vue'
import { AngleRight, AngleDown } from '@jd/jdesign-icons-vue'
import { JdButton } from '@jdesign-vue/components/button'
import { useNamespace } from '@jdesign-vue/hooks'
import { createColorVars, GROUP_DEFAULT_TITLE_WIDTH } from '../constants'
import type { StatisticCardSize, StatisticVNode } from '../types'

/**
 * 统一渲染 `StatisticVNode`：纯文本/数字直接输出，VNode 原样挂载，
 * 渲染函数先调用再输出。避免模板插值把 VNode 对象序列化成 JSON 字符串。
 */
const RenderNode = defineComponent({
  name: 'StatisticGroupHeaderNode',
  props: { content: { type: null, default: undefined } },
  setup(nodeProps) {
    return () => {
      const content = nodeProps.content as StatisticVNode
      return typeof content === 'function' ? content() : content
    }
  },
})

const props = withDefaults(defineProps<{
  title?: StatisticVNode
  description?: StatisticVNode
  extra?: StatisticVNode
  titlePlacement?: 'top' | 'left'
  titleAlign?: 'left' | 'center'
  collapsed?: boolean
  collapsible?: boolean
  groupTitleWidth?: number
  theme?: 'default' | 'brand' | (string & {})
  size?: StatisticCardSize
}>(), {
  titlePlacement: 'top',
  titleAlign: 'left',
  collapsed: false,
  collapsible: false,
  size: 'large',
})

const emit = defineEmits<{ (e: 'toggle'): void }>()

// VNode / 渲染函数同样视为「有内容」，但空串与 null/undefined 不渲染容器
const isPresent = (value: StatisticVNode) =>
  value !== undefined && value !== null && value !== '' && value !== false
const hasDescription = computed(() => isPresent(props.description))
const hasExtra = computed(() => isPresent(props.extra))

const ns = useNamespace('statistic-card')
const colorVars = createColorVars(ns.cssVarName)

const isTitleOnTop = computed(() => props.titlePlacement === 'top')
const titleFontSize = computed(() => props.size === 'large' ? 16 : 14)
const titleLineHeight = computed(() => props.size === 'large' ? '24px' : '22px')
const isCenter = computed(() => props.titleAlign === 'center')

const collapseIconStyle = computed<CSSProperties>(() => ({
  color: colorVars.textSecondary,
  fontSize: '12px',
  width: '12px',
  height: '12px',
}))

// ── left title styles ──
const leftTitleStyle = computed<CSSProperties>(() => {
  const titleWidth = props.groupTitleWidth ?? GROUP_DEFAULT_TITLE_WIDTH[props.size]
  return {
    width: titleWidth + 'px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    textAlign: 'center',
    boxSizing: 'border-box',
    color: colorVars.groupTitle,
  }
})

const leftTitleTextStyle = computed<CSSProperties>(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontSize: titleFontSize.value + 'px',
  fontWeight: 600,
  lineHeight: titleLineHeight.value,
  color: colorVars.groupTitle,
  wordBreak: 'break-all',
  textAlign: 'center',
}))

// ── top title styles ──
const topTitleStyle = computed<CSSProperties>(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: isCenter.value ? 'center' : 'flex-start',
  gap: '8px',
  paddingLeft: isCenter.value ? '0' : '4px',
  boxSizing: 'border-box',
  color: colorVars.groupTitle,
  position: 'relative',
}))

const topTitleInnerStyle = computed<CSSProperties>(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flex: isCenter.value ? 'none' : '1',
  minWidth: '0',
}))

const topTitleLabelStyle = computed<CSSProperties>(() => ({
  fontSize: titleFontSize.value + 'px',
  fontWeight: 600,
  lineHeight: titleLineHeight.value,
  color: colorVars.groupTitle,
  whiteSpace: 'nowrap',
  textAlign: props.titleAlign,
}))

const descriptionStyle = computed<CSSProperties>(() => ({
  fontSize: '12px',
  lineHeight: '18px',
  color: colorVars.groupDescription,
  whiteSpace: 'nowrap',
}))

const topExtraStyle = computed<CSSProperties>(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
  position: isCenter.value ? 'absolute' : 'static',
  right: isCenter.value ? '0' : undefined,
}))
</script>
